import React from 'react';
import {graphql} from 'react-relay';
import {Redirect} from 'react-router-dom';
import Styled from 'styled-components/macro';
import {ForecastQueryRenderer} from '../../ForecastQueryRenderer';
import CompanyViewHeader from './CompanyViewHeader';
import CompanyViewOverview from './CompanyViewOverview';

// The 1920px layout fills the content column fluidly. On ultra-wide screens it would otherwise
// sprawl edge-to-edge, so we cap + center the page content at >=2137px — there is no dedicated
// 2560px design, the 1920px layout just has to scale gracefully (IGNITE-479 AC). Mirrors the
// my-timesheets-page convention (2137px breakpoint).
const PageContent = Styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	width: 100%;
	@media screen and (min-width: 2137px) {
		max-width: 2089px;
		margin: 0 auto;
	}
`;

type ClientStatus = {
	id: string;
	standing: 'ACTIVE' | 'INACTIVE';
};

type PrimaryContact = {
	id: string;
	firstName: string | null;
	lastName: string | null;
};

type Client = {
	id: string;
	name: string;
	website: string | null;
	city: string | null;
	country: string | null;
	region: string | null;
	statusId: string | null;
	createdAt: string | null;
	updatedAt: string | null;
};

type InnerProps = {
	client: Client | null;
	status: ClientStatus | null;
	primaryContact: PrimaryContact | null;
	companyId: string;
};

export const CompanyViewPageInner: React.FC<InnerProps> = ({client, status, primaryContact, companyId}) => {
	if (!client) {
		// Invalid / non-existent client ID behind the feature flag → 404 (IGNITE-479 AC).
		return <Redirect to="/not-found" />;
	}
	return (
		<PageContent>
			<CompanyViewHeader client={client} status={status} primaryContact={primaryContact} companyId={companyId} />
			<CompanyViewOverview />
		</PageContent>
	);
};

const companyViewPageQuery = graphql`
	query CompanyViewPage_Query($clientId: String) {
		viewer {
			actualPersonId
			company {
				id
				client(id: $clientId) {
					id
					name
					website
					city
					country
					region
					statusId
					createdAt
					updatedAt
					status {
						id
						standing
					}
					primaryContact {
						id
						firstName
						lastName
					}
				}
			}
		}
	}
`;

type CompanyViewPageProps = {
	clientId: string;
};

const CompanyViewPage: React.FC<CompanyViewPageProps> = ({clientId}) => {
	return (
		<ForecastQueryRenderer
			key={'query-render-companyViewPage-' + clientId}
			query={companyViewPageQuery}
			variables={{clientId}}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			render={(relayProps: any) => {
				const company = relayProps?.viewer?.company;
				const client = company?.client ?? null;
				const status = client?.status ?? null;
				const primaryContact = client?.primaryContact ?? null;
				const companyId = company?.id ?? '';
				return (
					<CompanyViewPageInner
						client={client}
						status={status}
						primaryContact={primaryContact}
						companyId={companyId}
					/>
				);
			}}
		/>
	);
};

export default CompanyViewPage;
